import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Select,
  Button,
  Message,
  Space,
  InputNumber,
  Card,
  Divider,
  Upload,
  Tag,
  Checkbox,
  Typography,
  Alert
} from '@arco-design/web-react';
import {
  IconUpload,
  IconFile,
  IconCheck,
  IconClose,
  IconBook,
  IconPlus,
  IconDelete
} from '@arco-design/web-react/icon';
import { useSaveStudentDetails, useUploadDocument, useDeleteDocument } from '../hooks/useOnboardingData';
import ReportProblemButton from '../components/ReportProblemButton';
import './DocumentUploadCard.css';
import DocumentPreviewModal from './DocumentPreviewModal';

const FormItem = Form.Item;
const { Option } = Select;
const { TextArea } = Input;

const DEGREE_LEVEL_VALUES = ['secundaria', 'preparatoria', 'tsu', 'licenciatura', 'maestria', 'doctorado', 'postdoctorado'];

const DEGREE_HIERARCHY = {
  'secundaria': 1,
  'preparatoria': 2,
  'tsu': 3,
  'licenciatura': 4,
  'maestria': 5,
  'doctorado': 6,
  'postdoctorado': 7
};

const calculateHighestDegree = (degrees) => {
  if (!degrees || degrees.length === 0) return null;
  let highest = null;
  let highestValue = 0;
  degrees.forEach(degree => {
    const value = DEGREE_HIERARCHY[degree.level] || 0;
    if (value > highestValue) {
      highestValue = value;
      highest = degree.level;
    }
  });
  return highest;
};

const currentYear = new Date().getFullYear();

function StudentDetailsStep({
  onNext,
  onBack,
  initialData,
  documents: existingDocuments,
  programContext,
  requestedChanges = [],
  previewMode = false,
  freeMode = false
}) {
  const { t } = useTranslation('onboarding');

  const DEGREE_LEVELS = DEGREE_LEVEL_VALUES.map(value => ({
    value,
    label: t(`shared.degrees.${value}`)
  }));

  const saveMutation = useSaveStudentDetails();
  const draftMutation = useSaveStudentDetails({ partial: true });
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();
  const [degrees, setDegrees] = useState([{ level: '', institution: '', field: '', graduation_year: null }]);
  const [documents, setDocuments] = useState(existingDocuments || {});
  const [requestException, setRequestException] = useState(false);
  const [exceptionReason, setExceptionReason] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);

  const valorCurricular = programContext?.valor_curricular ?? initialData?.valor_curricular;

  useEffect(() => {
    if (initialData) {
      // Load degrees array
      if (initialData.degrees && initialData.degrees.length > 0) {
        setDegrees(initialData.degrees);
      }

      if (initialData.licenciatura_exception?.requested) {
        setRequestException(true);
        setExceptionReason(initialData.licenciatura_exception.reason || '');
      }
    }
  }, [initialData]);

  useEffect(() => {
    setDocuments(existingDocuments || {});
  }, [existingDocuments]);

  // Degree management
  const addDegree = () => {
    setDegrees(prev => [...prev, { level: '', institution: '', field: '', graduation_year: null }]);
  };

  const removeDegree = (index) => {
    setDegrees(prev => prev.filter((_, i) => i !== index));
    // Also delete associated document
    handleDocumentDelete(`degree_${index}`);
  };

  const updateDegree = (index, field, value) => {
    setDegrees(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const validateDegrees = () => {
    if (degrees.length === 0 || !degrees[0].level) {
      Message.error(t('shared.messages.addDegreeRequired'));
      return false;
    }

    const missingDegree = degrees.find((degree) => (
      !degree.level || !degree.institution || !degree.field || !degree.graduation_year
    ));
    if (missingDegree) {
      Message.error(t('shared.messages.completeDegreeData'));
      return false;
    }

    return true;
  };

  const buildPayload = () => {
    const payload = {
      highest_education_level: calculateHighestDegree(degrees),
      degrees: degrees,
      degree_count: degrees.length
    };
    if (valorCurricular && requestException) {
      payload.licenciatura_exception = {
        requested: true,
        reason: exceptionReason,
        proof_document: documents?.titulo_licenciatura_proof?.file_name || null
      };
    }
    return payload;
  };

  const handleSubmit = async () => {
    if (previewMode) {
      if (!freeMode && !validateDegrees()) return;
      onNext?.();
      return;
    }

    if (!validateDegrees()) return;

    saveMutation.mutate(buildPayload(), {
      onSuccess: () => {
        Message.success(t('steps.student.messages.dataSaved'));
        onNext?.();
      },
      onError: () => {
        Message.error(t('steps.student.messages.saveError'));
      }
    });
  };

  const handleSaveDraft = async () => {
    if (previewMode) return;

    draftMutation.mutate(buildPayload(), {
      onSuccess: () => {
        Message.success(t('steps.student.messages.draftSaved'));
      },
      onError: () => {
        Message.error(t('steps.student.messages.draftError'));
      }
    });
  };

  const handleDocumentUpload = (file, docType) => {
    if (previewMode) return;

    uploadMutation.mutate({ file, documentType: docType }, {
      onSuccess: (response) => {
        const result = response?.data || response;
        setDocuments(prev => ({
          ...prev,
          [docType]: result.document
        }));
        Message.success(t('steps.student.messages.documentUploaded'));
      },
      onError: () => {
        Message.error(t('steps.student.messages.documentUploadError'));
      }
    });
  };

  const handleDocumentDelete = (docType) => {
    if (previewMode) return;

    deleteMutation.mutate(docType, {
      onSuccess: () => {
        setDocuments(prev => {
          const next = { ...prev };
          delete next[docType];
          return next;
        });
        Message.success(t('shared.messages.documentDeleted'));
      }
    });
  };

  const needsTituloLicenciatura = valorCurricular === true;
  const hasTituloDoc = !!documents?.titulo_licenciatura;

  const renderUploadCard = ({ key, label, description, required, changeMessage }) => {
    const isUploaded = !!documents[key];

    return (
      <Card
        key={key}
        className={`document-upload-card${changeMessage ? ' document-upload-card--change' : ''}`}
        bordered
        style={{ marginBottom: 8 }}
      >
        <div className="document-card-header">
          <div className="document-title">
            <IconFile style={{ marginRight: 8 }} />
            <span>{label}</span>
            {required && <Tag color="red" size="small" style={{ marginLeft: 8 }}>{t('shared.labels.required')}</Tag>}
          </div>
          {isUploaded && <Tag color="green" icon={<IconCheck />}>{t('shared.labels.uploaded')}</Tag>}
        </div>

        {description && (
          <div className="document-description">
            <small>{description}</small>
          </div>
        )}

        {changeMessage && <div className="document-change-note">{changeMessage}</div>}

        {!isUploaded ? (
          <Upload
            accept=".pdf,.jpg,.jpeg,.png"
            autoUpload={false}
            showUploadList={false}
            onChange={(fileList, file) => {
              if (file.originFile) {
                handleDocumentUpload(file.originFile, key);
              }
            }}
          >
            <Button type="outline" icon={<IconUpload />} loading={uploadMutation.isPending && uploadMutation.variables?.documentType === key}>
              {t('shared.buttons.uploadDocument')}
            </Button>
          </Upload>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <small>{documents[key].file_name}</small>
            </div>
            <Space>
              <Button
                type="text"
                size="mini"
                onClick={() => setPreviewDoc({ type: key, ...documents[key] })}
              >
                {t('shared.labels.view')}
              </Button>
              <Button
                type="text"
                status="danger"
                icon={<IconClose />}
                onClick={() => handleDocumentDelete(key)}
              >
                {t('shared.labels.delete')}
              </Button>
            </Space>
          </div>
        )}

        <div className="document-help">
          <small>{t('shared.labels.fileFormats')}</small>
        </div>
      </Card>
    );
  };

  return (
    <div className="teacher-details-step">
      <div className="form-section">
        <h3>
          <IconBook style={{ marginRight: 8 }} />
          {t('shared.sections.academicBackground')}
        </h3>

        <Divider orientation="left">{t('shared.sections.academicDegrees', { count: degrees.length })}</Divider>

        {degrees.map((degree, index) => (
          <Card
            key={index}
            style={{ marginBottom: 16 }}
            title={t('shared.labels.degreeTitle', { number: index + 1 })}
            extra={
              degrees.length > 1 && (
                <Button
                  type="text"
                  status="danger"
                  icon={<IconDelete />}
                  onClick={() => removeDegree(index)}
                >
                  {t('shared.labels.delete')}
                </Button>
              )
            }
          >
            <FormItem label={t('shared.labels.level')} required>
              <Select
                placeholder={t('shared.placeholders.degreeLevel')}
                value={degree.level}
                onChange={(value) => updateDegree(index, 'level', value)}
              >
                {DEGREE_LEVELS.map(level => (
                  <Option key={level.value} value={level.value}>{level.label}</Option>
                ))}
              </Select>
            </FormItem>

            <FormItem label={t('shared.labels.institution')} required>
              <Input
                placeholder={t('shared.placeholders.institutionUNAM')}
                value={degree.institution}
                onChange={(value) => updateDegree(index, 'institution', value)}
              />
            </FormItem>

            <FormItem label={t('shared.labels.degree')} required>
              <Input
                placeholder={t('shared.placeholders.field')}
                value={degree.field}
                onChange={(value) => updateDegree(index, 'field', value)}
              />
            </FormItem>

            <FormItem label={t('shared.labels.graduationYear')} required>
              <InputNumber
                placeholder={t('shared.placeholders.year2020')}
                min={1950}
                max={currentYear}
                value={degree.graduation_year}
                onChange={(value) => updateDegree(index, 'graduation_year', value)}
                style={{ width: '100%' }}
              />
            </FormItem>

            <Divider orientation="left">{t('shared.documents.degreeDocTitle')}</Divider>
            {renderUploadCard({
              key: `degree_${index}`,
              label: t('shared.documents.degreeDocLabel', { number: index + 1 }),
              description: `${degree.level ? DEGREE_LEVELS.find(l => l.value === degree.level)?.label || degree.level : t('shared.labels.degreeGeneric')}${degree.field ? ` - ${degree.field}` : ''}${degree.institution ? ` (${degree.institution})` : ''}`.trim(),
              required: true
            })}
          </Card>
        ))}

        <Button
          type="dashed"
          long
          icon={<IconPlus />}
          onClick={addDegree}
        >
          {t('shared.buttons.addDegree')}
        </Button>

        {/* Conditional Document: Titulo Licenciatura */}
        {needsTituloLicenciatura && (
          <>
            <Divider orientation="left">{t('steps.student.labels.requiredDocuments')}</Divider>

            <Card style={{ marginBottom: 16 }}>
              <Space direction="vertical" size="medium" style={{ width: '100%' }}>
                <Space>
                  <Typography.Text bold>{t('steps.student.labels.bachelorTitle')}</Typography.Text>
                  <Tag color="orange">{t('steps.student.labels.vcRequired')}</Tag>
                </Space>

                {!requestException && (
                  <>
                    {hasTituloDoc ? (
                      <div className="document-upload-card">
                        <Space>
                          <IconCheck style={{ color: 'rgb(var(--green-6))' }} />
                          <Typography.Text>{documents.titulo_licenciatura.file_name}</Typography.Text>
                          <Button
                            type="text"
                            size="mini"
                            onClick={() => setPreviewDoc({ type: 'titulo_licenciatura', ...documents.titulo_licenciatura })}
                          >
                            {t('shared.labels.view')}
                          </Button>
                          <Button
                            type="text"
                            size="mini"
                            status="danger"
                            onClick={() => handleDocumentDelete('titulo_licenciatura')}
                          >
                            {t('shared.labels.delete')}
                          </Button>
                        </Space>
                      </div>
                    ) : (
                      <Upload
                        accept=".pdf,.jpg,.jpeg,.png"
                        autoUpload={false}
                        showUploadList={false}
                        onChange={(fileList, file) => {
                          if (file.originFile) {
                            handleDocumentUpload(file.originFile, 'titulo_licenciatura');
                          }
                        }}
                      >
                        <Button
                          type="outline"
                          icon={<IconUpload />}
                          loading={uploadMutation.isPending && uploadMutation.variables?.documentType === 'titulo_licenciatura'}
                        >
                          {t('steps.student.buttons.uploadBachelorTitle')}
                        </Button>
                      </Upload>
                    )}
                  </>
                )}

                <Checkbox
                  checked={requestException}
                  onChange={(checked) => {
                    setRequestException(checked);
                    if (!checked) {
                      setExceptionReason('');
                    }
                  }}
                >
                  {t('steps.student.labels.noDocument')}
                </Checkbox>

                {requestException && (
                  <div style={{ paddingLeft: 24 }}>
                    <Alert
                      type="warning"
                      content={t('steps.student.validation.exceptionAlert')}
                      style={{ marginBottom: 12 }}
                    />

                    <FormItem label={t('steps.student.labels.exceptionReason')} required>
                      <TextArea
                        placeholder={t('steps.student.placeholders.exceptionReason')}
                        value={exceptionReason}
                        onChange={setExceptionReason}
                        maxLength={2000}
                        showWordLimit
                        autoSize={{ minRows: 3 }}
                      />
                      {exceptionReason.length > 0 && exceptionReason.length < 50 && (
                        <Typography.Text type="error" style={{ fontSize: 12 }}>
                          {t('steps.student.validation.minChars', { current: exceptionReason.length })}
                        </Typography.Text>
                      )}
                    </FormItem>

                    <Typography.Text bold style={{ display: 'block', marginBottom: 8 }}>
                      {t('steps.student.labels.proofDocument')}
                    </Typography.Text>

                    {documents?.titulo_licenciatura_proof ? (
                      <div className="document-upload-card">
                        <Space>
                          <IconFile />
                          <Typography.Text>{documents.titulo_licenciatura_proof.file_name}</Typography.Text>
                          <Button
                            type="text"
                            size="mini"
                            status="danger"
                            onClick={() => handleDocumentDelete('titulo_licenciatura_proof')}
                          >
                            {t('shared.labels.delete')}
                          </Button>
                        </Space>
                      </div>
                    ) : (
                      <Upload
                        accept=".pdf,.jpg,.jpeg,.png"
                        autoUpload={false}
                        showUploadList={false}
                        onChange={(fileList, file) => {
                          if (file.originFile) {
                            handleDocumentUpload(file.originFile, 'titulo_licenciatura_proof');
                          }
                        }}
                      >
                        <Button
                          type="outline"
                          icon={<IconUpload />}
                          loading={uploadMutation.isPending && uploadMutation.variables?.documentType === 'titulo_licenciatura_proof'}
                        >
                          {t('steps.student.buttons.uploadProofDocument')}
                        </Button>
                      </Upload>
                    )}

                    <Tag color="orange" style={{ marginTop: 8 }}>
                      {t('steps.student.labels.requiresAdminApproval')}
                    </Tag>
                  </div>
                )}
              </Space>
            </Card>
          </>
        )}
      </div>

      {/* Navigation buttons - matching teacher pattern */}
      <FormItem>
        <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
          <Button onClick={() => onBack?.(1)} size="large">
            {t('shared.buttons.previous')}
          </Button>
          <Button
            type="primary"
            loading={saveMutation.isPending}
            onClick={handleSubmit}
            size="large"
            disabled={
              needsTituloLicenciatura &&
              requestException &&
              exceptionReason.length < 50
            }
          >
            {t('shared.buttons.continue')}
          </Button>
          <Button
            onClick={handleSaveDraft}
            loading={draftMutation.isPending}
            size="large"
            className="onboarding-draft-button"
          >
            {t('shared.buttons.saveDraft')}
          </Button>
          <ReportProblemButton stepLabel="Datos académicos" previewMode={previewMode} asButton />
        </Space>
      </FormItem>

      {previewDoc && (
        <DocumentPreviewModal
          visible={!!previewDoc}
          document={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}

export default StudentDetailsStep;
